import { Routes } from '@angular/router';
import { AuthenticateGuard } from './guards/authenticate.guard';
import { AuthorizeGuard } from './guards/authorize.guard';
import { UserPermissions } from './model';
import { DetermineHomePageGuard } from './guards/determine-home-page.guard';
import { TpaSearchComponent } from './features/tpa/components/tpa-search/tpa-search.component';
import { TpaDetailsComponent } from './features/tpa/components/tpa-details/tpa-details.component';
import { TpaDetailsGuard } from './guards/tpa-details.guard';

const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./components/login/login.component').then((mod) => mod.LoginComponent),
  },
  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./components/unauthorized/unauthorized.component').then(
        (mod) => mod.UnauthorizedComponent,
      ),
  },
  {
    path: '',
    canActivate: [AuthenticateGuard, DetermineHomePageGuard],
    loadComponent: () =>
      import('./core/layout/components/main-content/main-content.component').then(
        (mod) => mod.MainContentComponent,
      ),
  },
  {
    path: ':id',
    loadComponent: () =>
      import('./core/layout/components/main-content/main-content.component').then(
        (mod) => mod.MainContentComponent,
      ),
    children: [
      {
        path: 'policies',
        loadComponent: () =>
          import('./features/policy/policy-content-wrapper/policy-content-wrapper.component').then(
            (mod) => mod.PolicyContentWrapperComponent,
          ),
        canActivate: [AuthorizeGuard],
        data: { expectedPermission: UserPermissions.CensusManagement },
        children: [
          {
            path: ':id/census-lines',
            loadComponent: () =>
              import(
                './features/census/components/census-lines-view/census-lines-view.component'
              ).then((mod) => mod.CensusLinesViewComponent),
          },
          {
            path: 'table',
            loadComponent: () =>
              import('./features/policy/policies-table-view/policies-table-view.component').then(
                (mod) => mod.PoliciesTableViewComponent,
              ),
          },
          {
            path: '',
            loadComponent: () =>
              import('./features/policy/policies-view/policies-view.component').then(
                (mod) => mod.PoliciesViewComponent,
              ),
          },
        ],
      },
      {
        path: 'changerequests',
        loadComponent: () =>
          import(
            './features/change-request/components/census-change-content-wrapper/census-change-content-wrapper.component'
          ).then((mod) => mod.CensusChangeContentWrapperComponent),
        canActivate: [AuthorizeGuard],
        data: { expectedPermission: UserPermissions.CensusManagement },
        children: [
          {
            path: 'new',
            loadComponent: () =>
              import(
                './features/change-request/components/census-change-request/census-change-request.component'
              ).then((mod) => mod.CensusChangeRequestComponent),
          },
          {
            path: 'edit',
            loadComponent: () =>
              import(
                './features/change-request/components/census-change-request/census-change-request.component'
              ).then((mod) => mod.CensusChangeRequestComponent),
          },
          {
            path: '',
            loadComponent: () =>
              import(
                './features/change-request/components/census-change-requests-view/census-change-requests-view.component'
              ).then((mod) => mod.CensusChangeRequestsViewComponent),
          },
        ],
      },
      {
        path: 'quotes',
        loadComponent: () =>
          import(
            './features/quote/components/quote-content-wrapper/quote-content-wrapper.component'
          ).then((mod) => mod.QuoteContentWrapperComponent),
        canActivate: [AuthorizeGuard],
        data: { expectedPermission: UserPermissions.QuotingManagement },
        children: [
          {
            path: 'new',
            loadComponent: () =>
              import(
                './features/quote/components/quote-enrollment/quote-enrollment.component'
              ).then((mod) => mod.QuoteEnrollmentComponent),
          },
          {
            path: 'edit',
            loadComponent: () =>
              import(
                './features/quote/components/quote-enrollment/quote-enrollment.component'
              ).then((mod) => mod.QuoteEnrollmentComponent),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/quote/components/quote/quote.component').then(
                (mod) => mod.QuoteComponent,
              ),
          },
          {
            path: '',
            loadComponent: () =>
              import('./features/quote/components/quotes-view/quotes-view.component').then(
                (mod) => mod.QuotesViewComponent,
              ),
          },
        ],
      },
      {
        path: 'quote-groups',
        canActivate: [AuthorizeGuard],
        loadComponent: () =>
          import('./features/quote/components/quote-groups-view/quote-groups-view.component').then(
            (mod) => mod.QuoteGroupsViewComponent,
          ),
      },
      {
        path: 'uploads',
        loadComponent: () =>
          import(
            './features/upload/components/upload-content-wrapper/upload-content-wrapper.component'
          ).then((mod) => mod.UploadContentWrapperComponent),
        canActivate: [AuthorizeGuard],
        data: { expectedPermission: UserPermissions.UploadManagement },
        children: [
          {
            path: 'enrolls',
            loadComponent: () =>
              import('./features/upload/components/enroll-upload/enroll-upload.component').then(
                (mod) => mod.EnrollUploadComponent,
              ),
          },
          {
            path: 'enrollments',
            loadComponent: () =>
              import(
                './features/upload/components/enrollment-upload/enrollment-upload.component'
              ).then((mod) => mod.EnrollmentUploadComponent),
          },
          {
            path: 'persons',
            loadComponent: () =>
              import('./features/upload/components/person-upload/person-upload.component').then(
                (mod) => mod.PersonUploadComponent,
              ),
          },
        ],
      },
      {
        path: 'tpa',
        loadComponent: () =>
          import('./features/tpa/tpa-shell/tpa-shell.component').then(
            (mod) => mod.TpaShellComponent,
          ),
        canActivate: [AuthorizeGuard],
        data: { expectedPermission: UserPermissions.CensusSearch },
        children: [
          {
            path: 'search',
            component: TpaSearchComponent,
          },
          {
            path: 'details',
            component: TpaDetailsComponent,
            canActivate: [TpaDetailsGuard],
            data: {
              requiresSearchResults: true,
            },
          },
          {
            path: '',
            redirectTo: 'search',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'persons',
        loadComponent: () =>
          import('./features/person/components/person-content-wrapper/person-content-wrapper.component').then(
            (mod) => mod.PersonContentWrapperComponent,
          ),
        canActivate: [AuthorizeGuard],
        data: { expectedPermission: UserPermissions.CensusManagement },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/person/components/persons-view/persons-view.component').then(
                (mod) => mod.PersonsViewComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];

export default routes;
